/* ==========================================================================
   RIFA NMAX — Compra de paquetes + sorteo de los numeros.

   Al pulsar Comprar se sortean tantos numeros unicos de 4 cifras como incluya
   el paquete y viajan en la line item property `Tus numeros`. Desde ahi
   Shopify los arrastra al carrito, al checkout, al pedido y al **correo de
   confirmacion**, que renderiza las properties sin tocar la plantilla.

   Se sortean aqui y no despues del pago porque sin backend no hay forma de
   reaccionar al cobro: si se dejara para luego, el correo de confirmacion
   saldria sin numeros. Es lo que paso en la primera version.

   El comprador nunca elige: solo recibe lo que salga.
   ========================================================================== */

const TOTAL_NUMEROS = 10000; // 0000-9999

/** Numeros ya adjudicados, inyectados por Liquid. @returns {Set<string>} */
function leerVendidos() {
  const node = document.getElementById('rifa-vendidos');
  if (!node) return new Set();
  try {
    const lista = JSON.parse(node.textContent || '[]');
    return new Set(lista.map((n) => String(n).padStart(4, '0')));
  } catch (error) {
    console.warn('Rifa: lista de numeros vendidos ilegible', error);
    return new Set();
  }
}

/**
 * Sortea `cantidad` numeros unicos que no esten ya adjudicados.
 * Usa crypto cuando esta disponible: Math.random no es un sorteo defendible.
 * @param {number} cantidad
 * @param {Set<string>} vendidos
 * @returns {string[]}
 */
function sortearNumeros(cantidad, vendidos) {
  const elegidos = new Set();
  const disponibles = TOTAL_NUMEROS - vendidos.size;
  const aSortear = Math.min(cantidad, Math.max(disponibles, 0));

  // Camino rapido: mientras quede bombo de sobra, muestreo por rechazo.
  let intentos = 0;
  const maxIntentos = aSortear * 200 + 1000;

  while (elegidos.size < aSortear && intentos < maxIntentos) {
    intentos += 1;
    const n = String(azar(TOTAL_NUMEROS)).padStart(4, '0');
    if (vendidos.has(n) || elegidos.has(n)) continue;
    elegidos.add(n);
  }

  // Con el bombo casi agotado el rechazo falla mucho y devolveria menos
  // numeros de los pagados. Ahi se enumera lo que queda libre y se sortea
  // sobre esa lista: cuesta mas, pero nunca entrega de menos.
  if (elegidos.size < aSortear) {
    const libres = [];
    for (let n = 0; n < TOTAL_NUMEROS; n += 1) {
      const clave = String(n).padStart(4, '0');
      if (!vendidos.has(clave) && !elegidos.has(clave)) libres.push(clave);
    }
    while (elegidos.size < aSortear && libres.length) {
      elegidos.add(libres.splice(azar(libres.length), 1)[0]);
    }
  }

  return [...elegidos];
}

/** Entero uniforme en [0, max). @param {number} max @returns {number} */
function azar(max) {
  if (window.crypto?.getRandomValues) {
    // Rechazo del rango sobrante para no sesgar hacia los numeros bajos.
    const limite = Math.floor(0xffffffff / max) * max;
    const buffer = new Uint32Array(1);
    let valor;
    do {
      window.crypto.getRandomValues(buffer);
      valor = buffer[0];
    } while (valor >= limite);
    return valor % max;
  }
  return Math.floor(Math.random() * max);
}

class RifaPaquetes extends HTMLElement {
  #controller = null;
  #vendidos = new Set();

  connectedCallback() {
    this.#vendidos = leerVendidos();
    this.addEventListener('submit', (event) => this.#onSubmit(event));
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  /**
   * Rellena la property con los numeros sorteados.
   * @param {HTMLFormElement} form
   * @returns {string[]} los numeros asignados
   */
  #asignarNumeros(form) {
    const campo = form.querySelector('[data-numeros]');
    const cantidad = Number(form.querySelector('[name="properties[_cantidad]"]')?.value || 1);
    const numeros = sortearNumeros(cantidad, this.#vendidos);

    // Se reservan en memoria para que dos compras seguidas en la misma visita
    // no repitan numero.
    for (const n of numeros) this.#vendidos.add(n);

    if (campo) campo.value = numeros.join(', ');
    return numeros;
  }

  async #onSubmit(event) {
    const form = event.target.closest('form');
    if (!form) return;

    event.preventDefault();

    const numeros = this.#asignarNumeros(form);
    if (!numeros.length) {
      console.error('Rifa: no quedan numeros libres para asignar.');
      return;
    }

    const button = form.querySelector('button[type="submit"]');
    const label = button?.querySelector('.btn__label');
    const original = label?.textContent;

    if (button) button.disabled = true;
    if (label) label.textContent = 'Reservando…';

    this.#controller?.abort();
    this.#controller = new AbortController();

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
        signal: this.#controller.signal,
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.description || 'No se pudo agregar el paquete.');
      }

      window.location.href = '/checkout';
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('Rifa: alta al carrito fallida', error);

      // El envio nativo sigue siendo valido: los numeros ya estan en el campo.
      if (button) button.disabled = false;
      if (label && original) label.textContent = original;
      form.submit();
    }
  }
}

customElements.define('rifa-paquetes', RifaPaquetes);
